<template>
  <Teleport to="body">
    <div class="toast-container" v-if="toasts.length > 0">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast', `toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <CheckCircle v-if="toast.type === 'success'" :size="24" />
            <XCircle v-else-if="toast.type === 'error'" :size="24" />
            <AlertTriangle v-else-if="toast.type === 'warning'" :size="24" />
            <Info v-else :size="24" />
          </div>

          <div class="toast-content">
            <div class="toast-title" v-if="toast.title">{{ toast.title }}</div>
            <div class="toast-message" v-if="toast.message">{{ toast.message }}</div>
          </div>

          <button
            v-if="toast.dismissible"
            class="toast-close"
            @click="removeToast(toast.id)"
            aria-label="Close notification"
          >
            <X :size="18" />
          </button>

          <div
            v-if="toast.duration > 0"
            class="toast-progress"
            :style="{ animationDuration: `${toast.duration}ms` }"
          ></div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-vue-next'
import { useToast } from '@/composables/useToast'

const { toasts, removeToast } = useToast()
</script>

<style lang="scss" scoped>
.toast-container {
  position: fixed;
  top: 100px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: var(--background);
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
              0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border);
  min-width: 320px;
  max-width: 420px;
  position: relative;
  overflow: hidden;

  &.toast-success {
    border-left: 4px solid #10B981;

    .toast-icon {
      color: #10B981;
    }

    .toast-progress {
      background: #10B981;
    }
  }

  &.toast-error {
    border-left: 4px solid #EF4444;

    .toast-icon {
      color: #EF4444;
    }

    .toast-progress {
      background: #EF4444;
    }
  }

  &.toast-warning {
    border-left: 4px solid #F59E0B;

    .toast-icon {
      color: #F59E0B;
    }

    .toast-progress {
      background: #F59E0B;
    }
  }

  &.toast-info {
    border-left: 4px solid #3B82F6;

    .toast-icon {
      color: #3B82F6;
    }

    .toast-progress {
      background: #3B82F6;
    }
  }
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.toast-message {
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.toast-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  margin: -4px -8px -4px 0;

  &:hover {
    background: var(--background-secondary);
    color: var(--text-primary);
  }
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 0 0 0 12px;
  animation: toast-progress linear forwards;
}

@keyframes toast-progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

// Transitions
.toast-enter-active {
  animation: slide-in-right 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  animation: slide-out-right 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slide-in-right {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slide-out-right {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

@media (max-width: 768px) {
  .toast-container {
    right: 16px;
    left: 16px;
    top: 80px;
  }

  .toast {
    min-width: auto;
    max-width: none;
  }
}
</style>
